/**
 * @module app.core
 */

import { autoinject } from "aurelia-framework";
import { ApiModelIdentifier } from 'app/shared/models/essette-api';
import { StandardLoadOptions } from 'app/shared/lib/essette-site';
import { IApiActionResults, IApiQueryResults } from 'app/shared/lib/api/api-types';
import { Api } from 'app/shared/lib/essette-api';
import * as Core from 'app/core/models/essette-api';

export interface ICustomFieldSearchFilters {
    targetId?: string | null;
    targetType?: string | null;
    moduleCode?: string | null;
    targetCode?: string | null;
}

@autoinject
export class CustomFieldService {

    ////****************** Fields and Properties ************************************////


    ////****************** Functions ************************************************////
    constructor(
        private readonly api: Api) {
    }


    ////****************** Settings *************************************************////


    ////****************** Loads ****************************************************////
    public async loadCustomFields(searchFilters?: ICustomFieldSearchFilters, options?: StandardLoadOptions, includes: string[] = []): Promise<IApiQueryResults<Core.CustomField>> {
        const query = this.api
            .query('custom/fields')
            .include(...includes)
            .applyStandardLoadOptions(options);

        if(searchFilters?.moduleCode) {
            query.filterString('module.code', searchFilters.moduleCode)
        }
        if(searchFilters?.targetCode) {
            query.filterString('target.code', searchFilters.targetCode)
        }


        return await this.api.get(query);
    }

    public async loadCustomFieldTargets(searchFilters?: ICustomFieldSearchFilters, options?: StandardLoadOptions, includes: string[] = [], fields: string[] = []): Promise<IApiQueryResults<Core.CustomFieldTarget>> {
        if(!includes.includes('definitions')) { includes.push('definitions') }

        if(searchFilters?.targetType) {
            options = { ...options && { top: 1 } };
        }

        const query = this.api
            .query('custom/fields/targets')
            .applyStandardLoadOptions(options)
            .include(...includes)
            .field(...fields);

        if(searchFilters?.targetId) {
            query.filterIdentifier('id', searchFilters.targetId);
        }

        if(searchFilters?.targetType) {
            query.filterString('code', searchFilters.targetType, 'eq');
        }

        return await this.api.get(query);
    }

    // formerly loadCustomFieldsByDefinition
    public async loadCustomFieldDefinitionsByTarget(targetId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = []): Promise<IApiQueryResults<Core.CustomFieldDefinition>> {
        const query = this.api
            .query(`custom/fields/definitions`)
            .applyStandardLoadOptions(options)
            .filterString('target.id', targetId, 'eq')
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCustomFieldDefinition(definitionId: ApiModelIdentifier, includes: string[] = []): Promise<IApiQueryResults<Core.CustomFieldDefinition>> {
        const query = this.api
            .query(`custom/fields/definitions/${definitionId}`)
            .include(...includes);
        return await this.api.get(query);
    }

    public async loadCustomFieldChoicesByDefinition(targetCode: ApiModelIdentifier, definitionCode: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = []): Promise<IApiQueryResults<Core.CustomFieldChoice>> {
        const query = this.api
            .query(`custom/fields/choices`)
            .applyStandardLoadOptions(options)
            .filterString('definition.target.code', targetCode, 'eq')
            .filterString('definition.code', definitionCode, 'eq')
            .include(...includes);

        return await this.api.get(query);
    }

    public async loadCustomFieldChoicesByDefinitionId(definitionId: ApiModelIdentifier, options?: StandardLoadOptions, includes: string[] = []): Promise<IApiQueryResults<Core.CustomFieldChoice>> {
        const query = this.api
            .query(`custom/fields/definitions/${definitionId}/choices`)
            .applyStandardLoadOptions(options)
            .include(...includes);

        return await this.api.get(query);
    }


    ////****************** Actions **************************************************////

    public async activateCustomFieldDefinition(definitionId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const url = `custom/fields/definitions/${definitionId}`;
        const action = this.api.action(actionCode, url, { isActive })
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveCustomFieldDefinition(definition: Core.CustomFieldDefinition): Promise<IApiActionResults> {
        const actionName = !definition.id ? 'insert' : 'update';
        const action = this.api.action(actionName, `custom/fields/definitions/${actionName === 'update' ? definition.id : ''}`, definition);
        return await this.api.post(action);
    }

    public async activateCustomFieldChoice(choiceId: string, isActive: boolean): Promise<IApiActionResults> {
        const actionCode = isActive ? 'reactivate' : 'deactivate';
        const action = this.api.action(actionCode, `custom/fields/choices/${choiceId}`, { isActive: isActive });
        return isActive ? await this.api.post(action) : await this.api.delete(action);
    }

    public async saveCustomFieldChoice(choice: Core.CustomFieldChoice): Promise<IApiActionResults> {
        const actionName = !choice.id ? 'insert' : 'update';

        const action = this.api.action(actionName, `custom/fields/choices/${actionName === 'update' ? choice.id : ''}`, choice);

        return await this.api.post(action);
    }
}
